// The terminal skin: the same slot contract the native skin implements,
// drawn in a monospace register — glyph gutters, character-cell alignment,
// colour carrying status, and output indented under the call that made it.
//
// It is not a port of the TUI. No `Frame` crosses a wire, no ANSI is parsed,
// and no `tento-shapes` widget is reimplemented here. It reads the same
// `TurnView` the native skin reads and draws it differently; resemblance to
// the terminal is achieved by looking like it, not by consuming it.
//
// Structurally this file is the native skin's twin, which is the point: if
// registering a second skin had required anything the first did not, the
// contract would have been wrong.

import { TurnSlots } from "../thread/slots";
import { TermTurnAsk } from "./ask";
import { TermTurnAttachment } from "./attachment";
import { TermTurnBar } from "./bar";
import { TermTurnKvGrid } from "./kv-grid";
import { TermTurnNote } from "./note";
import { TermTurnPlan } from "./plan";
import { TermTurnPrompt } from "./prompt";
import { TermTurnProse } from "./prose";
import { TermTurnResult } from "./result";
import { TermTurnTermBlock } from "./term-block";
import { TermTurnThought } from "./thought";
import { TermTurnTool } from "./tool";

/** The terminal skin. `make` is exhaustive over `TurnSlots.Slot`; the default
 * arm binds `never`, so a slot added to the contract fails the build here
 * rather than rendering nothing at runtime. */
export const TERMINAL_SKIN: TurnSlots.Skin = {
  name: "terminal",
  make<S extends TurnSlots.Slot>(slot: S, props: TurnSlots.Props[S]): HTMLElement {
    switch (slot) {
      case "prompt":
        return TermTurnPrompt.makeWith(props as TurnSlots.Props["prompt"]);
      case "prose":
        return TermTurnProse.makeWith(props as TurnSlots.Props["prose"]);
      case "thought":
        return TermTurnThought.makeWith(props as TurnSlots.Props["thought"]);
      case "tool":
        return TermTurnTool.makeWith(props as TurnSlots.Props["tool"]);
      case "ask":
        return TermTurnAsk.makeWith(props as TurnSlots.Props["ask"]);
      case "plan":
        return TermTurnPlan.makeWith(props as TurnSlots.Props["plan"]);
      case "attachment":
        return TermTurnAttachment.makeWith(props as TurnSlots.Props["attachment"]);
      case "note":
        return TermTurnNote.makeWith(props as TurnSlots.Props["note"]);
      case "result":
        return TermTurnResult.makeWith(props as TurnSlots.Props["result"]);
      case "bar":
        return TermTurnBar.makeWith(props as TurnSlots.Props["bar"]);
      case "term-block":
        return TermTurnTermBlock.makeWith(props as TurnSlots.Props["term-block"]);
      case "kv-grid":
        return TermTurnKvGrid.makeWith(props as TurnSlots.Props["kv-grid"]);
      default: {
        const unreachable: never = slot;
        throw new Error(`terminal skin: unknown slot ${String(unreachable)}`);
      }
    }
  },
};
